/* eslint-disable camelcase */

import { useCallback } from 'react';

import type { AxiosResponse } from 'axios';

import type {
  Freelancer,
  FreelancerCompetences,
} from 'state/__generated__/tadaahAPI';
import { useApiFreelancerPartialUpdate } from 'state/__generated__/tadaahAPI';
import type { ErrorType } from 'state/custom-instance';
import client from 'state/remote/client';

import { useGetFreelancerDetails } from './use-get-freelancer-details';

export type UpdateFreelancerCompetencesType = {
  competences: FreelancerCompetences | undefined;
  hasDriverLicense: boolean;
  hasStintLicense: boolean;
};

type UseUpdateFreelancerCompetenciesHandler = {
  initialValues: UpdateFreelancerCompetencesType;
  initialValuesFetched: boolean;
  error: ErrorType<unknown> | null;
  isLoading: boolean;
  isSuccess: boolean;
  updateFreelancerCompetencies: (
    values: UpdateFreelancerCompetencesType,
  ) => Promise<AxiosResponse<Freelancer, unknown>>;
};

const useUpdateFreelancerCompetencies =
  (): UseUpdateFreelancerCompetenciesHandler => {
    const { data, isSuccess: initialValuesFetched } = useGetFreelancerDetails();
    const { mutateAsync, isLoading, isSuccess, error } =
      useApiFreelancerPartialUpdate({
        mutation: {
          mutationKey: 'update-freelancer-personal-information',
          onSuccess: () => client.invalidateQueries('freelancer-profile'),
        },
      });

    const updateFreelancerCompetencies = useCallback(
      async (values: UpdateFreelancerCompetencesType) => {
        const response = await mutateAsync({
          // @ts-expect-error wrong type information
          data: {
            competences: values.competences as FreelancerCompetences,
            has_driver_license: values.hasDriverLicense,
            has_stint_license: values.hasStintLicense,
          },
        });
        if (response.status !== 200) throw new Error('Something went wrong');
        else return response;
      },
      [mutateAsync],
    );

    const initialValues = {
      competences: data?.competences,
      hasDriverLicense: data?.has_driver_license ?? false,
      hasStintLicense: data?.has_stint_license ?? false,
    };
    return {
      error,
      initialValues,
      initialValuesFetched,
      isLoading,
      isSuccess,
      updateFreelancerCompetencies,
    };
  };

export default useUpdateFreelancerCompetencies;
